// File: src/components/music/MiniMusicPlayer.js
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useMusic } from "../../context/MusicContext";
import { useProfile } from "../../context/ProfileContext";
import { getDisplayArtistNames } from "../../Utils/song";
import { useNavigate } from "react-router-dom";

// 🖼️ Assets
import miniPlayIcon from "../../assets/mini_play.png";
import miniPauseIcon from "../../assets/mini_pause.png";
import previousDarkmodeIcon from "../../assets/previous_darkmode.png";
import nextDarkmodeIcon from "../../assets/next_darkmode.png";
import arrowLeftIcon from "../../assets/arrow-left.png";
import muteIcon from "../../assets/mute.png";
import unmuteIcon from "../../assets/unmute_active.png";

// 🌊 Compact 3-bar Audio Waveform for Micro Mode
const AudioWaveform = ({ isPlaying }) => {
  const bars = [0.4, 1, 0.6];

  return (
    <div className="flex items-center justify-center gap-[2px] h-4 w-full">
      {bars.map((scale, i) => (
        <motion.span
          key={i}
          className="w-[2px] bg-[#ff00cc] rounded-full"
          animate={
            isPlaying
              ? {
                  height: ["3px", `${scale * 13}px`, "3px"],
                }
              : { height: "3px" }
          }
          transition={
            isPlaying
              ? {
                  duration: 0.65 + (i % 2) * 0.15,
                  repeat: Infinity,
                  ease: "easeInOut",
                  repeatType: "reverse",
                  delay: i * 0.08,
                }
              : { duration: 0.2 }
          }
        />
      ))}
    </div>
  );
};

// 🌟 Reusable Marquee Component that scrolls ONLY on overflow
const MarqueeText = ({ children, className = "" }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [overflowDistance, setOverflowDistance] = useState(0);

  useEffect(() => {
    const calculateOverflow = () => {
      if (containerRef.current && textRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const textWidth = textRef.current.scrollWidth;

        if (textWidth > containerWidth) {
          setOverflowDistance(textWidth - containerWidth + 8);
        } else {
          setOverflowDistance(0);
        }
      }
    };

    calculateOverflow();
    window.addEventListener("resize", calculateOverflow);
    return () => window.removeEventListener("resize", calculateOverflow);
  }, [children]);

  const duration = Math.max(3.5, overflowDistance / 30);

  return (
    <div ref={containerRef} className="w-full overflow-hidden whitespace-nowrap relative">
      <motion.div
        ref={textRef}
        className={`inline-block whitespace-nowrap ${className}`}
        animate={
          overflowDistance > 0
            ? {
                x: [0, 0, -overflowDistance, -overflowDistance, 0],
              }
            : { x: 0 }
        }
        transition={
          overflowDistance > 0
            ? {
                duration: duration + 3,
                times: [0, 0.2, 0.7, 0.9, 1],
                repeat: Infinity,
                ease: "easeInOut",
              }
            : {}
        }
      >
        {children}
      </motion.div>
    </div>
  );
};

const MiniMusicPlayer = () => {
  const { isDarkMode } = useProfile();
  const {
    currentSong,
    isPlaying,
    togglePlayPause,
    playNext,
    playPrevious,
    audioRef,
    playSource,
    isMuted,
    toggleMute,
    isMicroPlayer,
    setIsMicroPlayer,
  } = useMusic();

  const navigate = useNavigate();

  const progressBarRef = useRef(null);
  const isDraggingCardRef = useRef(false); // 🔑 Prevents click trigger during/after swipe

  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPercent, setDragPercent] = useState(0);

  const isRadio = playSource === "Radio";
  const duration = audioRef?.current?.duration || 0;

  const handleToggleMute = (e) => {
    e.stopPropagation();
    toggleMute();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isDragging && audioRef?.current?.currentTime) {
        setCurrentTime(audioRef.current.currentTime);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [audioRef, isDragging]);

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleDrag = (clientX) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const ratio = x / rect.width;
    const newTime = ratio * duration;

    setDragPercent(ratio * 100);
    setCurrentTime(newTime);
  };

  const handleMouseDown = (e) => {
    if (!audioRef?.current || !duration) return;
    setIsDragging(true);
    handleDrag(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleDrag(e.clientX);
  };

  const handleMouseUp = () => {
    if (isDragging && audioRef?.current && duration) {
      audioRef.current.currentTime = (dragPercent / 100) * duration;
    }
    setIsDragging(false);
  };

  // 🌟 Handles Player Tap / Expand (strictly guarded against drag interactions)
  const handleExpandPlayer = () => {
    if (!currentSong) return;

    // 🛑 If this click was synthesized from a drag or swipe, ignore it!
    if (isDraggingCardRef.current) {
      return;
    }

    if (isMicroPlayer) {
      setIsMicroPlayer(false);
      return;
    }

    if (isRadio) {
      navigate("/radio");
    } else {
      navigate(`/song/${currentSong.id}`);
    }
  };

  if (!currentSong) return null;

  return (
    <div
      className={`w-full px-4 sm:px-6 pointer-events-auto flex items-end ${
        isMicroPlayer ? "justify-start" : "justify-center"
      }`}
    >
      <motion.div
        layout
        transition={{ type: "spring", stiffness: 350, damping: 30 }}
        drag="x"
        dragConstraints={
          isMicroPlayer ? { left: 0, right: 60 } : { left: -80, right: 0 }
        }
        dragElastic={{ left: isMicroPlayer ? 0 : 0.12, right: isMicroPlayer ? 0.12 : 0 }}
        dragSnapToOrigin
        onDragStart={() => {
          isDraggingCardRef.current = true;
        }}
        onDragEnd={(e, info) => {
          if (!isMicroPlayer && info.offset.x < -25) {
            setIsMicroPlayer(true);
          } else if (isMicroPlayer && info.offset.x > 15) {
            setIsMicroPlayer(false);
          }
          // Reset drag flag after mouse/touch sequence finishes
          setTimeout(() => {
            isDraggingCardRef.current = false;
          }, 80);
        }}
        onClick={handleExpandPlayer}
        className={`
          relative cursor-pointer mb-2 border backdrop-blur-md select-none overflow-hidden transition-colors duration-300
          ${
            isMicroPlayer
              ? "w-[66px] h-[30px] rounded-full p-[2px] pr-2 shadow-none flex-shrink-0"
              : "w-full max-w-xl h-auto rounded-xl px-3 py-2 shadow-2xl"
          }
          ${
            isDarkMode
              ? isMicroPlayer
                ? "bg-brand-gray/80 text-brand-white border-white/10"
                : "bg-brand-gray/50 text-brand-white border border-white/5"
              : isMicroPlayer
              ? "bg-brand-gray/80 text-white border-white/15"
              : "bg-brand-gray/60 text-white border border-white/10"
          }
        `}
      >
        {isMicroPlayer ? (
          /* ========================================================= */
          /* 1. MICRO PLAYER CONTENT                                   */
          /* ========================================================= */
          <motion.div
            key="micro-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="w-full h-full flex items-center justify-between"
          >
            <img
              src={currentSong.albumArtworkURL}
              alt="Art"
              className="w-[26px] h-[26px] rounded-full object-cover flex-shrink-0 select-none pointer-events-none"
            />

            <div className="flex-1 flex items-center justify-center">
              <AudioWaveform isPlaying={isPlaying} />
            </div>
          </motion.div>
        ) : (
          /* ========================================================= */
          /* 2. FULL MINI PLAYER CONTENT                               */
          /* ========================================================= */
          <motion.div
            key="mini-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="w-full flex items-center"
          >
            {/* Left Group: Arrow Button + Album Artwork */}
            <div className="flex items-center gap-1.5 flex-shrink-0 mr-3">
              <button
                type="button"
                title="Swipe left or tap to minimize"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMicroPlayer(true);
                }}
                className="p-1 hover:opacity-100 opacity-60 transition-opacity active:scale-90 focus:outline-none"
              >
                <img
                  src={arrowLeftIcon}
                  alt="Minimize"
                  className="w-3.5 h-3.5 object-contain"
                />
              </button>

              <img
                src={currentSong.albumArtworkURL}
                alt="Album Art"
                className="w-14 h-14 rounded-lg object-cover shadow-sm flex-shrink-0"
              />
            </div>

            {/* Metadata & Controls Column */}
            <div className="flex flex-col flex-grow overflow-hidden pr-1">
              <div className="flex items-center justify-between">
                
                {/* Titles */}
                <div className="flex flex-col justify-center overflow-hidden min-w-0 pr-2">
                  {isRadio && (
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <div className="relative w-2 h-2 flex-shrink-0 ml-0.5">
                        <span
                          className="animate-ping absolute inset-0 rounded-full bg-[#ff00cc] opacity-75"
                          style={{ animationDuration: "2s" }}
                        />
                        <span className="relative block w-2 h-2 rounded-full bg-[#ff00cc]" />
                      </div>
                      <span className="text-[9px] font-black uppercase tracking-widest text-pink/50">
                        Atomic Radio
                      </span>
                    </div>
                  )}

                  {/* Marquee Song Title */}
                  <div className="text-sm font-semibold leading-snug overflow-hidden whitespace-nowrap">
                    <MarqueeText>
                      {currentSong.title || "Unknown Title"}
                    </MarqueeText>
                  </div>

                  {/* Marquee Artist */}
                  <div className="text-xs text-white/60 leading-snug overflow-hidden whitespace-nowrap">
                    <MarqueeText className="pr-2">
                      {getDisplayArtistNames(currentSong)}
                    </MarqueeText>
                  </div>
                </div>

                {/* Media Deck Action Buttons */}
                <div className="flex items-center space-x-2.5 ml-2 flex-shrink-0">
                  {/* Previous Button */}
                  {!isRadio && (
                    <button
                      type="button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        playPrevious();
                      }}
                      className="p-1 hover:scale-105 active:scale-95 transition-transform focus:outline-none"
                      title="Previous"
                    >
                      <img
                        src={previousDarkmodeIcon}
                        alt="Previous"
                        className="w-5 h-5 object-contain pointer-events-none"
                      />
                    </button>
                  )}

                  {/* Play / Pause or Radio Mute */}
                  <button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={
                      isRadio
                        ? handleToggleMute
                        : (e) => {
                            e.stopPropagation();
                            togglePlayPause();
                          }
                    }
                    className="hover:scale-105 active:scale-95 transition-transform focus:outline-none p-0.5"
                    title={isPlaying ? "Pause" : "Play"}
                  >
                    <img
                      src={
                        isRadio
                          ? isMuted
                            ? muteIcon
                            : unmuteIcon
                          : isPlaying
                          ? miniPauseIcon
                          : miniPlayIcon
                      }
                      alt="Toggle Play"
                      className="w-7 h-7 object-contain select-none pointer-events-none"
                    />
                  </button>

                  {/* Next Button */}
                  {!isRadio && (
                    <button
                      type="button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        playNext();
                      }}
                      className="p-1 hover:scale-105 active:scale-95 transition-transform focus:outline-none"
                      title="Next"
                    >
                      <img
                        src={nextDarkmodeIcon}
                        alt="Next"
                        className="w-5 h-5 object-contain pointer-events-none"
                      />
                    </button>
                  )}
                </div>
              </div>

              {/* Progress Slider (Non-Radio only) */}
              {!isRadio && (
                <>
                  <div className="flex justify-between items-center text-xs text-white/60 w-full mt-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>

                  <div
                    className="mt-1 relative h-4 flex items-center cursor-pointer"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      ref={progressBarRef}
                      className="w-full h-1 bg-gray-700 rounded-full relative cursor-pointer"
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onTouchStart={(e) => handleMouseDown(e.touches[0])}
                      onTouchMove={(e) => handleMouseMove(e.touches[0])}
                      onTouchEnd={handleMouseUp}
                    >
                      <div
                        className="absolute top-0 left-0 h-full bg-[#ff00cc] rounded-full pointer-events-none"
                        style={{
                          width: `${
                            isDragging
                              ? dragPercent
                              : (currentTime / duration) * 100 || 0
                          }%`,
                          transition: "width 0.1s linear",
                        }}
                      />
                      <div
                        className="absolute w-3 h-3 bg-white rounded-full border border-gray-300 shadow-md z-10 pointer-events-none"
                        style={{
                          top: "50%",
                          left: `${
                            isDragging
                              ? dragPercent
                              : (currentTime / duration) * 100 || 0
                          }%`,
                          transform: "translate(-50%, -50%)",
                          transition: "left 0.1s linear",
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default MiniMusicPlayer;